import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { OnboardingBackButton } from '../../components/OnboardingBackButton';
import { clearPendingVenueReferralData, getPendingVenueReferralData } from '../../utils/deepLinkHandler';
import { getRoleSwitchContext } from '../../utils/roleSwitchFlow';

import { mapApplicationToOnboardingSession } from '../../utils/venueDraftResume';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VenueIntroPage() {
  const navigate = useNavigate();
  // "Adding another venue" mode: user already has at least one SUBMITTED
  // (non-draft) application and is creating a brand new one — skips
  // Step 1 (name) and runs the 6-step flow.
  const [isAddingAnother, setIsAddingAnother] = useState(false);
  // Pure draft-resume mode: user has an unsubmitted draft and just needs
  // to pick up where they left off. Stays in the full 7-step flow.
  const [hasDraft, setHasDraft] = useState(false);
  const [resumeStep, setResumeStep] = useState(1);

  useEffect(() => {
    const check = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const pendingVenueReferral = getPendingVenueReferralData();
        const onboardingState = JSON.parse(sessionStorage.getItem('venueOnboarding') || '{}');
        const roleSwitchContext = getRoleSwitchContext();
        if (pendingVenueReferral) {
          sessionStorage.setItem('venueOnboarding', JSON.stringify({
            ...onboardingState,
            invitedVenueReferralId: pendingVenueReferral.referral_id,
            invitedVenueName: pendingVenueReferral.venue_name,
            invitedVenueCategory: pendingVenueReferral.venue_category,
            invitedVenueAddress: pendingVenueReferral.venue_address,
            invitedVenueLink: pendingVenueReferral.venue_link,
            restaurantName: onboardingState.restaurantName || pendingVenueReferral.venue_name || '',
          }));
          clearPendingVenueReferralData();
        }
        if (onboardingState.isRoleSwitch || roleSwitchContext) {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          const [firstName = '', ...restNames] = (user.name || '').split(' ');
          setIsAddingAnother(true);
          sessionStorage.setItem('venueOnboarding', JSON.stringify({
            ...onboardingState,
            firstName: onboardingState.firstName || firstName,
            lastName: onboardingState.lastName || restNames.join(' '),
            email: onboardingState.email || user.email || '',
            isAddingVenue: true,
            isRoleSwitch: true,
          }));
          return;
        }
        const res = await axios.get(`${API}/venue/applications/my-all`, { headers: { Authorization: `Bearer ${token}` } });
        const apps = Array.isArray(res.data) ? res.data : [];
        if (apps.length === 0) return;

        // Split into "submitted" (active live/approved) and "resumable" (draft
        // or revisions_requested) buckets. A user whose only application is
        // resumable should pick up the SAME application in the wizard — they
        // aren't "adding another venue".
        const submitted = apps.filter((a) => !['draft', 'revisions_requested'].includes((a.status || '').toLowerCase()));
        const draft = apps.find((a) => ['draft', 'revisions_requested'].includes((a.status || '').toLowerCase()));

        if (draft) {
          // Resume the in-progress (draft or revisions_requested) application —
          // takes priority over starting a new one, even if the owner already
          // has other live venues.
          setHasDraft(true);
          const lastStep = Math.max(1, Math.min(2, Number(draft.current_step) || 1));
          setResumeStep(lastStep);
          const hydrated = mapApplicationToOnboardingSession(draft);
          sessionStorage.setItem('venueOnboarding', JSON.stringify(hydrated));
        } else if (submitted.length > 0) {
          // Returning owner adding another venue: skip Step 1 (name).
          setIsAddingAnother(true);
          const existing = submitted[0];
          const saved = sessionStorage.getItem('venueOnboarding');
          const parsed = saved ? JSON.parse(saved) : {};
          sessionStorage.setItem('venueOnboarding', JSON.stringify({
            ...parsed,
            firstName: existing.first_name || parsed.firstName || '',
            lastName: existing.last_name || parsed.lastName || '',
            phone: existing.phone || parsed.phone || '',
            phoneCountry: parsed.phoneCountry || { code: existing.phone_country_code || '+30' },
            isAddingVenue: true,
          }));
        }
      } catch (err) { /* not logged in or no venues */ }
    };
    check();
  }, []);

  const handleContinue = () => {
    // Fresh signups arrive from /select-role → /venue/intro without an
    // account yet. Send them through /register first; VerifyEmailPage
    // consumes the 'venue' partner intent and routes them back to
    // /venue/onboarding/1 after email confirmation. Existing users
    // already inside the app skip register and go through the normal
    // draft/resume decision tree below.
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    if (!token) {
      navigate('/register');
      return;
    }
    if (isAddingAnother) {
      // Owner is adding another venue — Step 1 still needs the business
      // details for the new venue (VAT, legal name, restaurant name), so
      // we send them there. Names are pre-filled from their previous
      // venue for a lighter touch.
      navigate('/venue/onboarding/1');
    } else if (hasDraft) {
      // Resume the draft at the step they left off.
      navigate(`/venue/onboarding/${resumeStep}`);
    } else {
      navigate('/venue/onboarding/1');
    }
  };

  const handleBack = () => {
    // Two entry points land users here:
    //   1. Fresh signup (email or Google): /select-role → tap "Food or
    //      Drink Venue" → /venue/intro. These users MUST go back to
    //      /select-role, even though they already have a token (Google
    //      auth mints one before they pick a role).
    //   2. Existing multi-role user: /switch-role → tap "Venue" →
    //      /venue/intro. Only these users should return to /switch-role.
    // Using token presence conflated the two, dropping fresh Google
    // signups on /switch-role — the multi-role hub is meaningless to
    // them because they haven't picked their first role yet.
    const cameFromSwitchRole = Boolean(getRoleSwitchContext());
    navigate(cameFromSwitchRole ? '/switch-role' : '/select-role');
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
      className="app-shell"
      data-testid="venue-intro-page"
      style={{
        minHeight: '100vh',
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: 'var(--app-bg)',
        position: 'relative',
        paddingLeft: 24,
        paddingRight: 24,
        paddingBottom: 36,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 'calc(env(safe-area-inset-top, 0px) + 20px)',
          left: 20,
        }}
      >
        <OnboardingBackButton
          onClick={handleBack}
          testId="venue-intro-back"
        />
      </div>

      <img
        src="/allure-logo.png"
        alt="Allure"
        style={{ width: 120, height: 108, objectFit: 'contain', marginTop: 56 }}
      />

      <div style={{ width: '100%', maxWidth: 360, marginTop: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.05 }}
          className="premium-card"
          data-testid="venue-intro-card-1"
          style={{ padding: '22px 22px' }}
        >
          <p
            style={{
              fontFamily: "'Inria Serif', serif",
              fontSize: 18,
              fontWeight: 400,
              color: 'var(--text)',
              lineHeight: 1.5,
              margin: 0,
              textAlign: 'center',
            }}
          >
            Allure is an exclusive guest platform designed for travelers staying in Airbnb homes, connecting them with carefully selected, top-tier venues that offer exceptional quality and unique experiences.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.12 }}
          className="premium-card"
          data-testid="venue-intro-card-2"
          style={{ padding: '22px 22px' }}
        >
          <p
            style={{
              fontFamily: "'Inria Serif', serif",
              fontSize: 18,
              fontWeight: 400,
              color: 'var(--text)',
              lineHeight: 1.5,
              margin: 0,
              textAlign: 'center',
            }}
          >
            We intentionally curate our partners to ensure guests receive a different, elevated experience.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.18 }}
          className="premium-card"
          data-testid="venue-intro-card-3"
          style={{
            padding: '24px 22px',
            background: 'linear-gradient(135deg, #161127 0%, #2A1B5A 100%)',
            color: '#FFFFFF',
            border: 'none',
          }}
        >
          <p
            style={{
              fontFamily: "'Inria Serif', serif",
              fontSize: 19,
              fontWeight: 700,
              color: '#FFFFFF',
              lineHeight: 1.4,
              margin: 0,
              textAlign: 'center',
            }}
          >
            Being featured on Allure positions your venue among the area&rsquo;s finest food and drink destinations.
          </p>
          <div
            style={{
              height: 1,
              background: 'rgba(255, 255, 255, 0.18)',
              margin: '18px 0',
            }}
            aria-hidden="true"
          />
          <p
            style={{
              fontFamily: "'Inria Serif', serif",
              fontSize: 17,
              fontWeight: 400,
              color: '#FFFFFF',
              lineHeight: 1.45,
              margin: 0,
              textAlign: 'center',
            }}
          >
            Meeting Allure&rsquo;s <strong style={{ fontWeight: 700 }}>standards</strong> is essential.
          </p>
        </motion.div>
      </div>

      <div style={{ flex: 1, minHeight: 32 }} />

      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={handleContinue}
        data-testid="venue-intro-next"
        className="premium-pill-button"
        style={{
          width: 220,
          padding: '14px 0',
          marginBottom: 40,
          fontFamily: "'Inria Serif', serif",
          fontSize: 20,
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        I Understand
      </motion.button>
    </motion.div>
  );
}
